import { useEffect, useMemo, useState } from 'react'
import { PageContainer, PageHeader } from '../../../components/Layout'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Select,
  EmptyState,
  Spinner
} from '../../../components/UI'
import { Download, FileText, Calendar, User, Building2, CheckCircle, AlertCircle, Send } from 'lucide-react'
import { paymentService, userService } from '../../../services/api'

export default function Extracts() {
  const [tenants, setTenants] = useState([])
  const [selectedTenant, setSelectedTenant] = useState('')
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [payments, setPayments] = useState([])
  const [loadingTenants, setLoadingTenants] = useState(true)
  const [loadingPayments, setLoadingPayments] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [sendingCertificate, setSendingCertificate] = useState(false)
  const [certificateStatus, setCertificateStatus] = useState(null)
  const [statusMessage, setStatusMessage] = useState('')
  const [error, setError] = useState('')

  const months = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
  ]

  const years = []
  const currentYear = new Date().getFullYear()
  for (let i = currentYear - 2; i <= currentYear + 1; i += 1) {
    years.push(i)
  }

  useEffect(() => {
    fetchTenants()
  }, [])

  useEffect(() => {
    if (!selectedTenant) {
      setPayments([])
      setCertificateStatus(null)
      setLoadingPayments(false)
      return
    }
    fetchPaymentsByTenant(selectedTenant)
  }, [selectedTenant])

  useEffect(() => {
    if (!selectedTenant) {
      setCertificateStatus(null)
      return
    }

    fetchCertificateStatus()
  }, [selectedTenant, selectedMonth, selectedYear])

  const fetchTenants = async () => {
    try {
      setLoadingTenants(true)
      setError('')

      const response = await userService.getTenants()
      const tenantList = response?.data?.data || []
      setTenants(tenantList)

      if (tenantList.length > 0) {
        const firstTenantId = String(tenantList[0].id || '')
        setSelectedTenant((prev) => {
          const exists = tenantList.some((t) => String(t.id || '') === String(prev || ''))
          return exists ? String(prev || '') : firstTenantId
        })
      } else {
        setSelectedTenant('')
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'No fue posible cargar arrendatarios')
      setTenants([])
      setSelectedTenant('')
    } finally {
      setLoadingTenants(false)
    }
  }

  const fetchPaymentsByTenant = async (tenantId) => {
    try {
      setLoadingPayments(true)
      setError('')
      const response = await paymentService.getAll({
        tenantId,
        page: 1,
        limit: 5000
      })
      setPayments(response?.data?.data?.payments || [])
    } catch (err) {
      setError(err?.response?.data?.message || 'No fue posible cargar pagos del arrendatario')
      setPayments([])
    } finally {
      setLoadingPayments(false)
    }
  }

  const fetchCertificateStatus = async () => {
    try {
      setError('')
      const response = await paymentService.getCertificateStatus({
        tenantId: selectedTenant,
        year: selectedYear,
        month: selectedMonth
      })
      setCertificateStatus(response?.data?.data || null)
    } catch (err) {
      setCertificateStatus(null)
      setError(err?.response?.data?.message || 'No fue posible cargar el estado de la cuenta de cobro')
    }
  }

  const filteredPayments = useMemo(() => {
    const selectedMonthEnd = new Date(selectedYear, selectedMonth, 0, 23, 59, 59, 999)
    const previousMonthEnd = new Date(selectedYear, selectedMonth - 1, 0, 23, 59, 59, 999)

    return payments
      .filter((payment) => {
        if (!payment?.due_date) return false

        const due = new Date(`${payment.due_date}T12:00:00`)
        const status = normalizeStatus(payment.status)
        const type = normalizeType(payment.type)
        const isService = type !== 'rent'

        if (isService) {
          return (status === 'pending' || status === 'overdue' || status === 'paid') && due <= previousMonthEnd
        }

        return (status === 'pending' || status === 'overdue') && due <= selectedMonthEnd
      })
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
  }, [payments, selectedMonth, selectedYear])

  const selectedTenantData = useMemo(
    () => tenants.find((t) => String(t.id || '').toLowerCase() === String(selectedTenant || '').toLowerCase()),
    [tenants, selectedTenant]
  )

  function normalizeStatus(value) {
    const raw = String(value || '').trim().toLowerCase()
    const map = {
      pendiente: 'pending',
      pending: 'pending',
      vencido: 'overdue',
      overdue: 'overdue',
      pagado: 'paid',
      paid: 'paid',
      aprobado: 'approved',
      approved: 'approved',
      cancelado: 'cancelled',
      cancelled: 'cancelled'
    }
    return map[raw] || raw
  }

  function normalizeType(value) {
    const raw = String(value || '').trim().toLowerCase()
    const map = {
      renta: 'rent',
      rent: 'rent',
      agua: 'water',
      water: 'water',
      luz: 'electricity',
      electricidad: 'electricity',
      electricity: 'electricity',
      gas: 'gas',
      mantenimiento: 'maintenance',
      maintenance: 'maintenance',
      deposito: 'deposit',
      depósito: 'deposit',
      deposit: 'deposit',
      otro: 'other',
      other: 'other'
    }
    return map[raw] || raw
  }

  const stats = useMemo(() => {
    const selectedMonthEnd = new Date(selectedYear, selectedMonth, 0, 23, 59, 59, 999)
    const previousMonthEnd = new Date(selectedYear, selectedMonth - 1, 0, 23, 59, 59, 999)

    let servicesDebt = 0
    let rentDebt = 0

    for (const payment of payments) {
      if (!payment?.due_date) continue
      const due = new Date(`${payment.due_date}T12:00:00`)
      const status = normalizeStatus(payment.status)
      const type = normalizeType(payment.type)
      const amount = Number(payment.amount || 0)

      const isService = type !== 'rent'
      const serviceStatusApplies = status === 'overdue' || status === 'pending' || status === 'paid'
      const rentStatusApplies = status === 'overdue' || status === 'pending'

      if (isService && serviceStatusApplies && due <= previousMonthEnd) {
        servicesDebt += amount
      }

      if (!isService && rentStatusApplies && due <= selectedMonthEnd) {
        rentDebt += amount
      }
    }

    const pendingBalance = servicesDebt + rentDebt

    return {
      servicesDebt,
      rentDebt,
      pendingBalance,
      peaceAndClear: pendingBalance === 0
    }
  }, [payments, selectedMonth, selectedYear])

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0)

  const formatDate = (value) => {
    if (!value) return '-'
    return new Date(value).toLocaleDateString('es-MX')
  }

  const monthLabel = months.find((m) => m.value === selectedMonth)?.label || ''

  const handleSendCertificate = async () => {
    if (!selectedTenant) {
      setError('Selecciona un arrendatario')
      return
    }

    try {
      setSendingCertificate(true)
      setError('')
      setStatusMessage('')

      await paymentService.sendCertificate({
        tenantId: selectedTenant,
        year: selectedYear,
        month: selectedMonth
      })

      setStatusMessage('Cuenta de cobro enviada por correo.')
      await fetchCertificateStatus()
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'No fue posible enviar la cuenta de cobro')
    } finally {
      setSendingCertificate(false)
    }
  }

  const handleGenerateCertificate = () => {
    if (!selectedTenant) {
      setError('Selecciona un arrendatario')
      return
    }

    setGenerating(true)
    setError('')

    try {
      const fullName = `${selectedTenantData?.first_name || ''} ${selectedTenantData?.last_name || ''}`.trim() || 'Sin nombre'

      const rowsHtml = filteredPayments.length > 0
        ? filteredPayments
            .map((payment) => `
              <tr>
                <td>${formatDate(payment.due_date)}</td>
                <td>${payment.type || '-'}</td>
                <td>${formatCurrency(payment.amount)}</td>
                <td>${payment.status || '-'}</td>
              </tr>
            `)
            .join('')
        : '<tr><td colspan="4">No hay pagos para el periodo seleccionado</td></tr>'

      const html = `
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Cuenta de cobro</title>
  <style>
    @page { size: A4; margin: 16mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; color: #111827; background: #f8fafc; }
    .sheet { max-width: 980px; margin: 0 auto; background: #ffffff; border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; }
    .header { padding: 16px 20px; border-bottom: 1px solid #e5e7eb; background: #ffffff; }
    .title { font-size: 22px; font-weight: 700; margin: 0 0 2px; color: #111827; }
    .subtitle { margin: 0; font-size: 13px; color: #6b7280; }
    .content { padding: 18px 20px 14px; }
    .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; }
    .panel { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 12px; background: #ffffff; }
    .panel h3 { margin: 0 0 8px; font-size: 12px; color: #374151; text-transform: uppercase; letter-spacing: 0.04em; }
    .kv { display: grid; grid-template-columns: 110px 1fr; gap: 6px 10px; font-size: 12px; }
    .k { color: #6b7280; }
    .v { color: #111827; font-weight: 600; }
    .summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 12px; }
    .summary-card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 9px 10px; background: #fafafa; }
    .summary-label { font-size: 11px; color: #6b7280; margin-bottom: 4px; }
    .summary-value { font-size: 16px; font-weight: 700; color: #111827; }
    .ok { color: #047857; font-weight: 700; }
    .warn { color: #b45309; font-weight: 700; }
    .table-wrap { border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
    table { width: 100%; border-collapse: collapse; }
    th, td { text-align: left; padding: 8px 9px; font-size: 12px; border-bottom: 1px solid #eef2f7; }
    th { background: #f3f4f6; color: #111827; font-weight: 700; text-transform: uppercase; font-size: 11px; letter-spacing: 0.04em; }
    tr:last-child td { border-bottom: none; }
    .foot { padding: 10px 20px 14px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 10px; display: flex; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="header">
      <h1 class="title">Cuenta de Cobro</h1>
      <p class="subtitle">Periodo: ${monthLabel} ${selectedYear}</p>
    </div>

    <div class="content">
      <div class="grid-2">
        <div class="panel">
          <h3>Datos del Arrendatario</h3>
          <div class="kv">
            <div class="k">Nombre</div><div class="v">${fullName}</div>
            <div class="k">Email</div><div class="v">${selectedTenantData?.email || '-'}</div>
          </div>
        </div>
        <div class="panel">
          <h3>Datos del Reporte</h3>
          <div class="kv">
            <div class="k">Mes</div><div class="v">${monthLabel}</div>
            <div class="k">Año</div><div class="v">${selectedYear}</div>
          </div>
        </div>
      </div>

      <div class="summary">
        <div class="summary-card">
          <div class="summary-label">Servicios (mes anterior)</div>
          <div class="summary-value">${formatCurrency(stats.servicesDebt)}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Renta (desde mes generado)</div>
          <div class="summary-value">${formatCurrency(stats.rentDebt)}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Con Saldo Pendiente</div>
          <div class="summary-value ${stats.peaceAndClear ? 'ok' : 'warn'}">${formatCurrency(stats.pendingBalance)}</div>
        </div>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Tipo</th>
              <th>Monto</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </div>
    </div>

    <div class="foot">
      <div>Generado por RentalBiz</div>
      <div>Documento generado el ${new Date().toLocaleString('es-MX')}</div>
    </div>
  </div>
</body>
</html>`

      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        setError('El navegador bloqueó la ventana de impresión. Habilita popups e intenta de nuevo.')
        return
      }

      printWindow.document.open()
      printWindow.document.write(html)
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()
    } finally {
      setGenerating(false)
    }
  }

  if (loadingTenants) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-[400px]">
          <Spinner size="xl" />
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <PageHeader
          title="Cuentas de Cobro"
          subtitle="Genera la cuenta de cobro filtrada por arrendatario"
        />

        {error && (
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-red-700">{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {statusMessage && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-green-700">{statusMessage}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {tenants.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <EmptyState
                icon={FileText}
                title="No hay arrendatarios disponibles"
                description="Necesitas al menos un arrendatario para generar cuentas de cobro"
              />
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <User className="w-5 h-5 mr-2 text-primary-600" />
                      Filtro por arrendatario
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Select
                      label="Arrendatario"
                      value={selectedTenant}
                      onChange={(e) => setSelectedTenant(e.target.value)}
                      options={tenants.map((tenant) => {
                        const name = `${tenant.first_name || ''} ${tenant.last_name || ''}`.trim() || tenant.email
                        return { value: String(tenant.id || ''), label: `${name} - ${tenant.email || tenant.id}` }
                      })}
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <Select
                        label="Mes"
                        value={String(selectedMonth)}
                        onChange={(e) => setSelectedMonth(Number.parseInt(e.target.value, 10))}
                        options={months.map((month) => ({ value: String(month.value), label: month.label }))}
                      />
                      <Select
                        label="Año"
                        value={String(selectedYear)}
                        onChange={(e) => setSelectedYear(Number.parseInt(e.target.value, 10))}
                        options={years.map((year) => ({ value: String(year), label: String(year) }))}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Calendar className="w-5 h-5 mr-2 text-primary-600" />
                      Resumen del periodo
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm"><span>Servicios (mes anterior)</span><strong>{formatCurrency(stats.servicesDebt)}</strong></div>
                      <div className="flex justify-between text-sm"><span>Renta (desde mes generado)</span><strong>{formatCurrency(stats.rentDebt)}</strong></div>
                      <div className="flex justify-between text-sm"><span>Con Saldo Pendiente</span><strong>{formatCurrency(stats.pendingBalance)}</strong></div>
                      <div className="flex justify-between text-sm">
                        <span>Envio por correo</span>
                        <span className={certificateStatus?.can_send ? 'text-green-700 font-semibold' : 'text-yellow-700 font-semibold'}>
                          {certificateStatus?.can_send ? 'Habilitado' : certificateStatus?.sent ? 'Enviado' : 'No habilitado'}
                        </span>
                      </div>
                      {certificateStatus?.disabled_reason && (
                        <p className="text-xs text-gray-500">{certificateStatus.disabled_reason}</p>
                      )}
                      {certificateStatus?.recipient_email && (
                        <p className="text-xs text-gray-500">Destino: {certificateStatus.recipient_email}</p>
                      )}
                      {certificateStatus?.carbon_copy_email && (
                        <p className="text-xs text-gray-500">CC: {certificateStatus.carbon_copy_email}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card className="bg-gradient-to-br from-blue-50 to-primary-50 border-primary-200">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="w-5 h-5 text-primary-700" />
                      <h4 className="font-semibold text-primary-900">Cuenta de cobro</h4>
                    </div>
                    <p className="text-sm text-primary-800">
                      Se genera con filtros por arrendatario, mes y año.
                    </p>
                  </CardContent>
                </Card>

                <Button
                  variant="primary"
                  size="lg"
                  className="w-full"
                  onClick={handleSendCertificate}
                  loading={sendingCertificate}
                  disabled={sendingCertificate || loadingPayments || !selectedTenant || !certificateStatus?.can_send}
                >
                  <Send className="w-5 h-5 mr-2" />
                  {certificateStatus?.sent ? 'Cuenta de cobro enviada' : 'Enviar cuenta de cobro por correo'}
                </Button>

                <Button
                  variant="primary"
                  size="lg"
                  className="w-full"
                  onClick={handleGenerateCertificate}
                  loading={generating}
                  disabled={generating || loadingPayments || !selectedTenant}
                >
                  <Download className="w-5 h-5 mr-2" />
                  Generar cuenta de cobro
                </Button>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-primary-600" />
                  Pagos del arrendatario ({filteredPayments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingPayments ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner size="lg" />
                  </div>
                ) : filteredPayments.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500">No hay pagos para el arrendatario y periodo seleccionados.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 text-gray-600">
                          <th className="py-2 text-left">Fecha</th>
                          <th className="py-2 text-left">Tipo</th>
                          <th className="py-2 text-left">Monto</th>
                          <th className="py-2 text-left">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPayments.map((payment) => (
                          <tr key={payment.id} className="border-b border-gray-100">
                            <td className="py-2">{formatDate(payment.due_date)}</td>
                            <td className="py-2 capitalize">{payment.type || '-'}</td>
                            <td className="py-2">{formatCurrency(payment.amount)}</td>
                            <td className="py-2 capitalize">{payment.status || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </PageContainer>
  )
}
